import React, { useMemo, useEffect } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';

/**
 * Loads a GLTF/GLB file and returns a processed clone with:
 *   - bounding-box centering at world origin
 *   - auto-scale to fit a 3-unit envelope
 *   - shadow casting/receiving on every mesh
 *   - optional wireframe (materials are cloned, never mutated in the GLTF cache)
 *
 * Suspense: this component suspends until the model is ready, so it must be
 * wrapped in <Suspense> by the parent.
 *
 * @param {string}   url          — public path or object URL to .glb / .gltf
 * @param {boolean}  centerModel  — translate so bounding-box center = world origin
 * @param {boolean}  fitToView    — uniform-scale to fit within a 3-unit envelope
 * @param {number}   scale        — additional uniform scale multiplier
 * @param {boolean}  wireframe    — render all meshes in wireframe
 * @param {boolean}  shadows      — enable castShadow / receiveShadow
 * @param {Function} onModelLoad  — called with the processed Object3D after mount
 */
function GLTFModel({
  url,
  centerModel = true,
  fitToView = true,
  scale = 1,
  wireframe = false,
  shadows = true,
  onModelLoad,
}) {
  // `true` enables the DRACO decoder (loaded from the drei CDN) so Draco-compressed
  // GLBs — a very common export format — don't silently fail to load.
  const { scene } = useGLTF(url, true);

  const processedScene = useMemo(() => {
    // Clone the cached scene so we never mutate shared GLTF data.
    // Geometries remain shared (memory-efficient); only Object3D nodes are duplicated.
    const cloned = scene.clone(true);

    const box = new THREE.Box3().setFromObject(cloned);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    // Resolve the uniform scale first so we can offset by the *scaled* center.
    let scaleFactor = scale;
    if (fitToView) {
      const maxDimension = Math.max(size.x, size.y, size.z);
      if (maxDimension > 0) {
        scaleFactor = (3 / maxDimension) * scale;
      }
    }
    cloned.scale.setScalar(scaleFactor);

    if (centerModel) {
      // The transform matrix applies scale before translation, so the center
      // offset must be scaled too — otherwise the model drifts off origin.
      cloned.position.copy(center).multiplyScalar(-scaleFactor);
    }

    cloned.traverse((child) => {
      if (!child.isMesh) return;

      child.castShadow = shadows;
      child.receiveShadow = shadows;

      if (wireframe) {
        // Clone material(s) before setting wireframe to avoid affecting the GLTF cache.
        const applyWireframe = (mat) => {
          const m = mat.clone();
          m.wireframe = true;
          return m;
        };
        child.material = Array.isArray(child.material)
          ? child.material.map(applyWireframe)
          : applyWireframe(child.material);
      }
    });

    return cloned;
  }, [scene, centerModel, fitToView, scale, wireframe, shadows]);

  useEffect(() => {
    onModelLoad?.(processedScene);
  }, [processedScene, onModelLoad]);

  return <primitive object={processedScene} />;
}

export default GLTFModel;
